import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import html2canvas from "html2canvas";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";
import toast from "react-hot-toast";
import { useAuth } from "../../../auth/AuthContext";
import { retrieveUsersApi } from "../../../components/api/UserApiService";
import {
  retrieveTravelCalendarEntriesByUserIdAndDateRangeApi,
  retrieveTravelCalendarEntriesByDateRangeApi,
  createTravelCalendarEntryApi,
  createMultipleTravelCalendarEntriesApi,
  updateTravelCalendarEntryApi,
  deleteMultipleTravelCalendarEntriesApi,
  retrieveSharedSpecialOccasionsApi,
  updateApprovalStatusApi,
  bulkUpdateApprovalStatusApi,
  getCalendarDelegateAccessApi,
  retrievePendingApprovalEntriesApi,
} from "../../../components/api/TravelCalendarApiService";
import CalendarToolbar from "./CalendarToolbar";
import CalendarTable from "./CalendarTable";
import YearlyCalendar from "./YearlyCalendar";
import { EntryDialog, MultiAddDialog } from "./EntryDialogs";
import { InspectDialog, DisapproveDialog, BulkDisapproveDialog, ConflictPreviewDialog, DateOverviewDialog } from "./CalendarDialogs";
import DelegateManagementDialog from "./DelegateManagementDialog";
import PendingApprovalPanel from "./PendingApprovalPanel";

// Extract HH:mm from a datetime string without timezone conversion
const extractTime = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  const str = String(dateTimeStr);
  const match = str.match(/T(\d{2}:\d{2})/);
  if (match) return match[1];
  const timeOnly = str.match(/^(\d{2}:\d{2})/);
  if (timeOnly) return timeOnly[1];
  return "";
};

const EMPTY_ENTRY = {
  destination: "",
  flight: "",
  etd: "",
  eta: "",
  comment: "",
  others: "",
  inEu: false,
  inSin: false,
  specialOccasion: "",
  sharedSpecialOccasion: false,
  approvalStatus: "WAITING_FOR_APPROVAL",
  entryType: "TEMPLATE",
  halfDayType: "NONE",
  category: "",
  flights: [{ destination: "", flight: "", etd: "", eta: "" }],
};

const TravelCalendarView = () => {
  // Refs for screenshot
  const tableRef = useRef(null);
  const yearlyRef = useRef(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null); // { dataUrl, filename }

  // Screenshot handler for both views — opens preview instead of auto-downloading
  const handleExportScreenshot = async () => {
    if (viewMode === "monthly") {
      if (!tableRef.current) return;
      const tableContainer = tableRef.current.querySelector('[data-screenshot-table]');
      if (!tableContainer) return;
      const table = tableContainer.querySelector('table');
      if (!table) return;
      const prevMaxHeight = tableContainer.style.maxHeight;
      const prevOverflow = tableContainer.style.overflow;
      const prevTableWidth = table.style.width;
      tableContainer.style.maxHeight = 'none';
      tableContainer.style.overflow = 'visible';
      tableContainer.scrollTop = 0;
      tableContainer.scrollLeft = 0;
      table.style.width = table.scrollWidth + 'px';
      const ths = tableRef.current.querySelectorAll('th, td');
      const prevSticky = [];
      ths.forEach((th) => {
        prevSticky.push({
          el: th,
          position: th.style.position,
          top: th.style.top,
          zIndex: th.style.zIndex,
        });
        th.style.position = '';
        th.style.top = '';
        th.style.zIndex = '';
      });
      await new Promise((res) => setTimeout(res, 100));
      await html2canvas(tableRef.current, {
        useCORS: true,
        backgroundColor: '#fff',
        scale: 2,
        width: table.scrollWidth,
        height: tableContainer.scrollHeight,
        windowWidth: table.scrollWidth,
        windowHeight: tableContainer.scrollHeight,
      }).then((canvas) => {
        setScreenshotPreview({ dataUrl: canvas.toDataURL('image/png'), filename: `${exportFileName}.png` });
      });
      prevSticky.forEach(({ el, position, top, zIndex }) => {
        el.style.position = position;
        el.style.top = top;
        el.style.zIndex = zIndex;
      });
      tableContainer.style.maxHeight = prevMaxHeight;
      tableContainer.style.overflow = prevOverflow;
      table.style.width = prevTableWidth;
    } else if (viewMode === "yearly") {
      if (!yearlyRef.current) return;
      await html2canvas(yearlyRef.current, { backgroundColor: '#fff' }).then((canvas) => {
        setScreenshotPreview({
          dataUrl: canvas.toDataURL('image/png'),
          filename: `yearly-calendar-${selectedYear}-${exportFileName}.png`,
        });
      });
    }
  };

  const handleScreenshotDownload = () => {
    if (!screenshotPreview) return;
    const link = document.createElement('a');
    link.download = screenshotPreview.filename;
    link.href = screenshotPreview.dataUrl;
    link.click();
  };
  const { user: currentUser, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [sharedOccasions, setSharedOccasions] = useState([]);

  // Add/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_ENTRY });
  const [selectedDate, setSelectedDate] = useState(null);

  // Multi-add dialog state
  const [multiAddOpen, setMultiAddOpen] = useState(false);
  const [multiSelectedDates, setMultiSelectedDates] = useState([]);
  const [multiFormData, setMultiFormData] = useState({ ...EMPTY_ENTRY });
  const [multiAddMonth, setMultiAddMonth] = useState(new Date());
  const [multiAddEntries, setMultiAddEntries] = useState([]);

  // Disapprove dialog state
  const [disapproveDialogOpen, setDisapproveDialogOpen] = useState(false);
  const [disapproveEntryId, setDisapproveEntryId] = useState(null);
  const [disapproveNote, setDisapproveNote] = useState("");

  // Inspect dialog state
  const [inspectDialogOpen, setInspectDialogOpen] = useState(false);
  const [inspectEntry, setInspectEntry] = useState(null);
  const [inspectDate, setInspectDate] = useState(null);

  // Conflict dialog state
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictingEntries, setConflictingEntries] = useState([]);
  const [pendingSavePayload, setPendingSavePayload] = useState(null);
  const [pendingSaveType, setPendingSaveType] = useState(null); // 'single' | 'multi'

  // Bulk selection state
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [bulkDisapproveOpen, setBulkDisapproveOpen] = useState(false);
  const [bulkDisapproveNote, setBulkDisapproveNote] = useState("");

  // View mode state
  const [viewMode, setViewMode] = useState("monthly"); // "monthly" | "yearly"
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Date filter state
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // Multi-edit mode state
  const [multiEditMode, setMultiEditMode] = useState(false);
  const [editableData, setEditableData] = useState({});
  const [multiEditSaving, setMultiEditSaving] = useState(false);

  // Delegate access state
  const [myDelegateAccessList, setMyDelegateAccessList] = useState([]); // calendars I can edit as a delegate
  const [manageDelegatesOpen, setManageDelegatesOpen] = useState(false);

  // Pending approval panel state
  const [pendingEntries, setPendingEntries] = useState([]);

  // Date overview dialog state
  const [dateOverviewOpen, setDateOverviewOpen] = useState(false);
  const [dateOverviewDate, setDateOverviewDate] = useState(null);
  const [dateOverviewEntries, setDateOverviewEntries] = useState([]);
  const [dateOverviewLoading, setDateOverviewLoading] = useState(false);

  // ─── Derived data ───

  const selectedUser = users.find((u) => u.id === selectedUserId);
  const isOwnCalendar = currentUser?.id === selectedUserId;
  const isAdminOrManager = (currentUser?.roles || []).some(
    (r) => r === "ADMIN" || r === "MANAGER" || r === "ROLE_ADMIN" || r === "ROLE_MANAGER" || r === "SCOPE_ROLE_ADMIN" || r === "SCOPE_ROLE_MANAGER"
  );
  const isManager = (currentUser?.roles || []).some(
    (r) => r === "MANAGER" || r === "ROLE_MANAGER" || r === "SCOPE_ROLE_MANAGER"
  );
  // true when the current user is a delegate for the selected user's calendar
  const isDelegateForSelectedUser = !isOwnCalendar && myDelegateAccessList.some((a) => a.userId === selectedUserId);
  // true when user can add/edit/delete entries on the viewed calendar
  const canEdit = isOwnCalendar || isDelegateForSelectedUser;

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  // Apply date filter
  const filteredDaysInMonth = useMemo(() => {
    if (!filterStartDate && !filterEndDate) return daysInMonth;
    return daysInMonth.filter((date) => {
      if (filterStartDate && format(date, "yyyy-MM-dd") < filterStartDate) return false;
      if (filterEndDate && format(date, "yyyy-MM-dd") > filterEndDate) return false;
      return true;
    });
  }, [daysInMonth, filterStartDate, filterEndDate]);

  const entriesByDate = useMemo(() => {
    const map = {};
    entries.forEach((e) => {
      if (e.entryDate) {
        const key = e.entryDate.substring(0, 10);
        if (!map[key]) map[key] = [];
        map[key].push(e);
      }
    });
    return map;
  }, [entries]);

  // Alias to the primary (first) entry per date – used by multi-edit mode and excel export
  const entryByDate = useMemo(() => {
    const map = {};
    Object.entries(entriesByDate).forEach(([key, arr]) => {
      if (arr.length > 0) map[key] = arr[0];
    });
    return map;
  }, [entriesByDate]);

  // Build export data
  const excelData = useMemo(() => {
    return filteredDaysInMonth.map((date) => {
      const dateStr = format(date, "yyyy-MM-dd");
      const entry = entryByDate[dateStr];
      return {
        Date: format(date, "dd/MM/yyyy"),
        Day: format(date, "EEEE"),
        Destination: entry?.destination || "",
        Flight: entry?.flight || "",
        ETD: extractTime(entry?.etd) || "",
        ETA: extractTime(entry?.eta) || "",
        Comment: entry?.comment || "",
        Others: entry?.others || "",
        EU: entry?.inEu ? "Yes" : "",
        Singapore: entry?.inSin ? "Yes" : "",
        "Special Occasion": entry?.specialOccasion || "",
        Status: entry?.approvalStatus?.replace(/_/g, " ") || "",
        Type: entry?.entryType || "",
      };
    });
  }, [filteredDaysInMonth, entryByDate]);

  const exportFileName = useMemo(() => {
    const userName = selectedUser?.name || selectedUser?.username || "User";
    return `TravelCalendar_${userName}_${format(currentMonth, "yyyy-MM")}`;
  }, [selectedUser, currentMonth]);

  const bulkSelectableEntries = entries.filter((e) => e && e.id);
  const allSelected =
    bulkSelectableEntries.length > 0 &&
    bulkSelectableEntries.every((e) => selectedEntryIds.includes(e.id));

  const showCheckboxes = !multiEditMode && (canEdit || isAdminOrManager);

  // ─── Data loading ───

  useEffect(() => {
    if (currentUser?.id && !selectedUserId) {
      setSelectedUserId(currentUser.id);
    }
  }, [currentUser]);

  useEffect(() => {
    retrieveUsersApi()
      .then((res) => {
        const activeUsers = (res?.data || []).filter(
          (u) => !u.recordStatus || u.recordStatus === "ACTIVE"
        );
        setUsers(activeUsers);
      })
      .catch((err) => console.error("Error loading users:", err));
  }, []);

  // Load calendars the current user has been granted delegate access to
  useEffect(() => {
    if (!currentUser?.id) return;
    getCalendarDelegateAccessApi()
      .then((res) => setMyDelegateAccessList(res?.data || []))
      .catch((err) => console.error("Error loading delegate access:", err));
  }, [currentUser?.id]);

  // Load pending approval entries for admins/managers
  const loadPendingEntries = useCallback(() => {
    if (!isAdminOrManager) return;
    retrievePendingApprovalEntriesApi()
      .then((res) => setPendingEntries(res?.data || []))
      .catch(() => setPendingEntries([]));
  }, [isAdminOrManager]);

  useEffect(() => {
    loadPendingEntries();
  }, [loadPendingEntries]);

  const loadEntries = useCallback(() => {
    if (!selectedUserId) return;
    const start = format(startOfMonth(currentMonth), "yyyy-MM-dd");
    const end = format(endOfMonth(currentMonth), "yyyy-MM-dd");

    retrieveTravelCalendarEntriesByUserIdAndDateRangeApi(selectedUserId, start, end)
      .then((res) => setEntries(res?.data || []))
      .catch((err) => console.error("Error loading entries:", err));

    retrieveSharedSpecialOccasionsApi(start, end)
      .then((res) => setSharedOccasions(res?.data || []))
      .catch((err) => console.error("Error loading shared occasions:", err));
  }, [selectedUserId, currentMonth]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const loadMultiAddEntries = (month) => {
    if (!selectedUserId) return;
    const start = format(startOfMonth(month), "yyyy-MM-dd");
    const end = format(endOfMonth(month), "yyyy-MM-dd");
    retrieveTravelCalendarEntriesByUserIdAndDateRangeApi(selectedUserId, start, end)
      .then((res) => setMultiAddEntries(res?.data || []))
      .catch(() => setMultiAddEntries([]));
  };

  // ─── Navigation handlers ───

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setSelectedEntryIds([]);
    setFilterStartDate("");
    setFilterEndDate("");
  };
  const handleNextMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setSelectedEntryIds([]);
    setFilterStartDate("");
    setFilterEndDate("");
  };
  const handleToday = () => {
    setCurrentMonth(new Date());
    setViewMode("monthly");
    setSelectedEntryIds([]);
    setFilterStartDate("");
    setFilterEndDate("");
  };
  const handleUserChange = (e) => {
    setSelectedUserId(e.target.value);
    setSelectedEntryIds([]);
  };

  // ─── Single entry CRUD ───

  const handleAddEntry = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const existingEntries = entriesByDate[dateStr] || [];
    setEditingEntry(null);
    setSelectedDate(date);
    // If one half-day entry already exists, pre-select the complementary half
    let defaultHalfDayType = "NONE";
    if (existingEntries.length === 1) {
      const existingType = existingEntries[0].halfDayType;
      if (existingType === "FIRST") defaultHalfDayType = "LAST";
      else if (existingType === "LAST") defaultHalfDayType = "FIRST";
    }
    setFormData({ ...EMPTY_ENTRY, halfDayType: defaultHalfDayType });
    setDialogOpen(true);
  };

  const handleEditEntry = (entry, date) => {
    setEditingEntry(entry);
    setSelectedDate(date);
    // Build flights array: prefer child flights, fall back to flat fields
    const entryFlights =
      entry.flights && entry.flights.length > 0
        ? entry.flights.map((f) => ({
            destination: f.destination || "",
            flight: f.flight || "",
            etd: extractTime(f.etd),
            eta: extractTime(f.eta),
          }))
        : [{
            destination: entry.destination || "",
            flight: entry.flight || "",
            etd: extractTime(entry.etd),
            eta: extractTime(entry.eta),
          }];
    setFormData({
      destination: entry.destination || "",
      flight: entry.flight || "",
      etd: extractTime(entry.etd),
      eta: extractTime(entry.eta),
      comment: entry.comment || "",
      others: entry.others || "",
      inEu: entry.inEu || false,
      inSin: entry.inSin || false,
      specialOccasion: entry.specialOccasion || "",
      sharedSpecialOccasion: entry.sharedSpecialOccasion || false,
      approvalStatus: entry.approvalStatus || "WAITING_FOR_APPROVAL",
      entryType: entry.entryType || "ACTUAL",
        halfDayType: entry.halfDayType || "NONE",
      category: entry.category || "",
      flights: entryFlights,
    });
    setDialogOpen(true);
  };

  const handleDeleteEntry = (entryId) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    deleteMultipleTravelCalendarEntriesApi([entryId])
      .then(() => {
        toast.success("Entry deleted.");
        loadEntries();
      })
      .catch(() => toast.error("Failed to delete entry."));
  };

  const handleSave = () => {
    if (!formData.category) {
      toast.error("Please select a category.");
      return;
    }
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    // Validate half-day combinations for new entries
    if (!editingEntry) {
      const existingDateEntries = entriesByDate[dateStr] || [];
      if (existingDateEntries.length > 0) {
        const newType = formData.halfDayType || "NONE";
        const existingTypes = existingDateEntries.map((e) => e.halfDayType || "NONE");
        if (newType === "NONE" || existingTypes.includes("NONE")) {
          toast.error("A full-day entry already exists for this date. Delete it first to add half-day entries.");
          return;
        }
        if (existingTypes.includes(newType)) {
          toast.error(`A ${newType === "FIRST" ? "first" : "second"} half-day entry already exists for this date.`);
          return;
        }
        if (existingDateEntries.length >= 2) {
          toast.error("This date already has two entries.");
          return;
        }
      }
    }
    const flights = (formData.flights || [{ destination: formData.destination, flight: formData.flight, etd: formData.etd, eta: formData.eta }])
      .map((f) => ({
        destination: f.destination || null,
        flight: f.flight || null,
        etd: f.etd ? `${dateStr}T${f.etd}:00` : null,
        eta: f.eta ? `${dateStr}T${f.eta}:00` : null,
      }));
    const firstFlight = flights[0] || {};
    const payload = {
      userId: selectedUserId,
      entryDate: dateStr,
      destination: firstFlight.destination || null,
      flight: firstFlight.flight || null,
      etd: firstFlight.etd || null,
      eta: firstFlight.eta || null,
      comment: formData.comment || null,
      others: formData.others || null,
      inEu: formData.inEu,
      inSin: formData.inSin,
      specialOccasion: formData.specialOccasion || null,
      sharedSpecialOccasion: formData.sharedSpecialOccasion,
      approvalStatus: editingEntry ? "WAITING_FOR_APPROVAL" : (formData.approvalStatus || "WAITING_FOR_APPROVAL"),
      entryType: formData.entryType,
      recordStatus: "ACTIVE",
        halfDayType: formData.halfDayType || "NONE",
      category: formData.category || null,
      flights,
    };

    if (editingEntry) {
      updateTravelCalendarEntryApi(editingEntry.id, payload)
        .then(() => {
          toast.success("Entry updated.");
          setDialogOpen(false);
          loadEntries();
        })
        .catch(() => toast.error("Failed to update entry."));
    } else {
      // Check for other users' entries on the same date before saving
      retrieveTravelCalendarEntriesByDateRangeApi(dateStr, dateStr)
        .then((res) => {
          const others = (res?.data || []).filter(
            (e) => e.user && e.user.id !== selectedUserId
          );
          if (others.length > 0) {
            setConflictingEntries(others);
            setPendingSavePayload(payload);
            setPendingSaveType("single");
            setConflictDialogOpen(true);
          } else {
            createTravelCalendarEntryApi(payload)
              .then(() => {
                toast.success("Entry created.");
                setDialogOpen(false);
                loadEntries();
              })
              .catch(() => toast.error("Failed to create entry."));
          }
        })
        .catch(() => {
          // If conflict check fails, proceed with save anyway
          createTravelCalendarEntryApi(payload)
            .then(() => {
              toast.success("Entry created.");
              setDialogOpen(false);
              loadEntries();
            })
            .catch(() => toast.error("Failed to create entry."));
        });
    }
  };

  // ─── Multi-add handlers ───

  const handleOpenMultiAdd = () => {
    setMultiSelectedDates([]);
    setMultiFormData({ ...EMPTY_ENTRY });
    setMultiAddMonth(new Date(currentMonth));
    setMultiAddOpen(true);
    loadMultiAddEntries(new Date(currentMonth));
  };

  const handleToggleMultiDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    setMultiSelectedDates((prev) =>
      prev.includes(dateStr) ? prev.filter((d) => d !== dateStr) : [...prev, dateStr]
    );
  };

  const handleMultiAddMonthChange = (direction) => {
    const newMonth = new Date(multiAddMonth.getFullYear(), multiAddMonth.getMonth() + direction, 1);
    setMultiAddMonth(newMonth);
    setMultiSelectedDates([]);
    loadMultiAddEntries(newMonth);
  };

  const handleMultiSave = () => {
    if (multiSelectedDates.length === 0) {
      toast.error("Please select at least one date.");
      return;
    }
    if (!multiFormData.category) {
      toast.error("Please select a category.");
      return;
    }
    const sortedDates = [...multiSelectedDates].sort();
    const payloads = sortedDates.map((dateStr) => {
      const flights = (multiFormData.flights || [{ destination: multiFormData.destination, flight: multiFormData.flight, etd: multiFormData.etd, eta: multiFormData.eta }])
        .map((f) => ({
          destination: f.destination || null,
          flight: f.flight || null,
          etd: f.etd ? `${dateStr}T${f.etd}:00` : null,
          eta: f.eta ? `${dateStr}T${f.eta}:00` : null,
        }));
      const firstFlight = flights[0] || {};
      return {
        userId: selectedUserId,
        entryDate: dateStr,
        destination: firstFlight.destination || null,
        flight: firstFlight.flight || null,
        etd: firstFlight.etd || null,
        eta: firstFlight.eta || null,
        comment: multiFormData.comment || null,
        others: multiFormData.others || null,
        inEu: multiFormData.inEu,
        inSin: multiFormData.inSin,
        specialOccasion: multiFormData.specialOccasion || null,
        sharedSpecialOccasion: multiFormData.sharedSpecialOccasion,
        approvalStatus: multiFormData.approvalStatus || "WAITING_FOR_APPROVAL",
        entryType: multiFormData.entryType,
        recordStatus: "ACTIVE",
        halfDayType: multiFormData.halfDayType || "NONE",
        category: multiFormData.category || null,
        flights,
      };
    });

    // Check for other users' entries on any of the selected dates
    retrieveTravelCalendarEntriesByDateRangeApi(sortedDates[0], sortedDates[sortedDates.length - 1])
      .then((res) => {
        const selectedDateSet = new Set(sortedDates);
        const others = (res?.data || []).filter(
          (e) =>
            e.user &&
            e.user.id !== selectedUserId &&
            e.entryDate &&
            selectedDateSet.has(e.entryDate.substring(0, 10))
        );
        if (others.length > 0) {
          setConflictingEntries(others);
          setPendingSavePayload(payloads);
          setPendingSaveType("multi");
          setConflictDialogOpen(true);
        } else {
          createMultipleTravelCalendarEntriesApi(payloads)
            .then(() => {
              toast.success(`${sortedDates.length} entries created.`);
              setMultiAddOpen(false);
              loadEntries();
            })
            .catch(() => toast.error("Failed to create entries."));
        }
      })
      .catch(() => {
        // If conflict check fails, proceed with save anyway
        createMultipleTravelCalendarEntriesApi(payloads)
          .then(() => {
            toast.success(`${sortedDates.length} entries created.`);
            setMultiAddOpen(false);
            loadEntries();
          })
          .catch(() => toast.error("Failed to create entries."));
      });
  };

  // ─── Conflict dialog handlers ───

  const handleConflictProceed = () => {
    setConflictDialogOpen(false);
    const payload = pendingSavePayload;
    const type = pendingSaveType;
    setPendingSavePayload(null);
    setPendingSaveType(null);
    setConflictingEntries([]);
    if (type === "single") {
      createTravelCalendarEntryApi(payload)
        .then(() => {
          toast.success("Entry created.");
          setDialogOpen(false);
          loadEntries();
        })
        .catch(() => toast.error("Failed to create entry."));
    } else if (type === "multi") {
      createMultipleTravelCalendarEntriesApi(payload)
        .then(() => {
          toast.success(`${payload.length} entries created.`);
          setMultiAddOpen(false);
          loadEntries();
        })
        .catch(() => toast.error("Failed to create entries."));
    } else if (type === "multiedit") {
      executeMultiEditSave(payload);
    }
  };

  const handleConflictGoBack = () => {
    setConflictDialogOpen(false);
    setPendingSavePayload(null);
    setPendingSaveType(null);
    setConflictingEntries([]);
    // Entry dialog (or multi-add dialog) stays open with all data intact
  };

  // ─── Approval handlers ───

  const handleApproval = (entryId, status) => {
    updateApprovalStatusApi(entryId, status)
      .then(() => {
        toast.success("Entry approved.");
        loadEntries();
        loadPendingEntries();
      })
      .catch(() => toast.error("Failed to update approval status."));
  };

  // Navigate to user/month when clicking a pending entry, then open inspect dialog
  const handlePendingEntryNavigate = (entry) => {
    if (entry.user?.id) setSelectedUserId(entry.user.id);
    if (entry.entryDate) {
      const d = new Date(entry.entryDate);
      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
    setViewMode("monthly");
    setInspectEntry(entry);
    setInspectDate(entry.entryDate ? new Date(entry.entryDate) : null);
    setInspectDialogOpen(true);
  };

  const handlePendingApprove = (entryId) => {
    updateApprovalStatusApi(entryId, "APPROVED")
      .then(() => {
        toast.success("Entry approved.");
        loadPendingEntries();
        // Refresh main table only if this entry belongs to the currently viewed calendar
        const entry = pendingEntries.find((e) => e.id === entryId);
        if (entry?.user?.id === selectedUserId) loadEntries();
      })
      .catch(() => toast.error("Failed to approve entry."));
  };

  const handleOpenDisapprove = (entryId) => {
    setDisapproveEntryId(entryId);
    setDisapproveNote("");
    setDisapproveDialogOpen(true);
  };

  const handleConfirmDisapprove = () => {
    if (!disapproveEntryId) return;
    updateApprovalStatusApi(disapproveEntryId, "NOT_APPROVED", disapproveNote)
      .then(() => {
        toast.success("Entry disapproved.");
        setDisapproveDialogOpen(false);
        loadEntries();
      })
      .catch(() => toast.error("Failed to disapprove entry."));
  };

  const handleInspectEntry = (entry, date) => {
    setInspectEntry(entry);
    setInspectDate(date);
    setInspectDialogOpen(true);
  };

  const handleInspectApprove = (entryId) => {
    updateApprovalStatusApi(entryId, "APPROVED")
      .then(() => {
        toast.success("Entry approved.");
        // Refresh inspect entry state so dialog reflects new status immediately
        setInspectEntry((prev) => prev ? { ...prev, approvalStatus: "APPROVED" } : prev);
        loadEntries();
        loadPendingEntries();
      })
      .catch(() => toast.error("Failed to approve entry."));
  };

  const handleInspectDisapprove = (entryId, note) => {
    updateApprovalStatusApi(entryId, "NOT_APPROVED", note)
      .then(() => {
        toast.success("Entry disapproved.");
        setInspectEntry((prev) => prev ? { ...prev, approvalStatus: "NOT_APPROVED", disapprovalNote: note } : prev);
        loadEntries();
        loadPendingEntries();
      })
      .catch(() => toast.error("Failed to disapprove entry."));
  };

  // ─── Date overview handler ───

  const handleDateOverview = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    setDateOverviewDate(date);
    setDateOverviewEntries([]);
    setDateOverviewLoading(true);
    setDateOverviewOpen(true);
    retrieveTravelCalendarEntriesByDateRangeApi(dateStr, dateStr)
      .then((res) => setDateOverviewEntries(res?.data || []))
      .catch(() => setDateOverviewEntries([]))
      .finally(() => setDateOverviewLoading(false));
  };

  // ─── Bulk selection ───

  const handleToggleBulkSelect = (entryId) => {
    setSelectedEntryIds((prev) =>
      prev.includes(entryId) ? prev.filter((id) => id !== entryId) : [...prev, entryId]
    );
  };

  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelectedEntryIds([]);
    } else {
      setSelectedEntryIds(bulkSelectableEntries.map((e) => e.id));
    }
  };

  const handleBulkApprove = () => {
    if (selectedEntryIds.length === 0) {
      toast.error("Please select at least one entry.");
      return;
    }
    const actualIds = selectedEntryIds.filter((id) =>
      entries.some((e) => e.id === id && e.entryType === "ACTUAL")
    );
    const skipped = selectedEntryIds.length - actualIds.length;
    if (actualIds.length === 0) {
      toast.error("None of the selected entries are Actual — only Actual entries can be approved.");
      return;
    }
    bulkUpdateApprovalStatusApi(actualIds, "APPROVED")
      .then(() => {
        if (skipped > 0) {
          toast.success(`${actualIds.length} entries approved. ${skipped} Template ${skipped === 1 ? "entry was" : "entries were"} skipped.`);
        } else {
          toast.success(`${actualIds.length} entries approved.`);
        }
        setSelectedEntryIds([]);
        loadEntries();
      })
      .catch(() => toast.error("Failed to approve entries."));
  };

  const handleOpenBulkDisapprove = () => {
    if (selectedEntryIds.length === 0) {
      toast.error("Please select at least one entry.");
      return;
    }
    const actualIds = selectedEntryIds.filter((id) =>
      entries.some((e) => e.id === id && e.entryType === "ACTUAL")
    );
    if (actualIds.length === 0) {
      toast.error("None of the selected entries are Actual — only Actual entries can be disapproved.");
      return;
    }
    setBulkDisapproveNote("");
    setBulkDisapproveOpen(true);
  };

  const handleConfirmBulkDisapprove = () => {
    const actualIds = selectedEntryIds.filter((id) =>
      entries.some((e) => e.id === id && e.entryType === "ACTUAL")
    );
    const skipped = selectedEntryIds.length - actualIds.length;
    bulkUpdateApprovalStatusApi(actualIds, "NOT_APPROVED", bulkDisapproveNote)
      .then(() => {
        if (skipped > 0) {
          toast.success(`${actualIds.length} entries disapproved. ${skipped} Template ${skipped === 1 ? "entry was" : "entries were"} skipped.`);
        } else {
          toast.success(`${actualIds.length} entries disapproved.`);
        }
        setBulkDisapproveOpen(false);
        setSelectedEntryIds([]);
        loadEntries();
      })
      .catch(() => toast.error("Failed to disapprove entries."));
  };

  const handleBulkDelete = () => {
    if (selectedEntryIds.length === 0) {
      toast.error("Please select at least one entry.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedEntryIds.length} entries?`)) return;
    deleteMultipleTravelCalendarEntriesApi(selectedEntryIds)
      .then(() => {
        toast.success(`${selectedEntryIds.length} entries deleted.`);
        setSelectedEntryIds([]);
        loadEntries();
      })
      .catch(() => toast.error("Failed to delete entries."));
  };

  // ─── Multi-edit mode ───

  const handleEnterMultiEdit = () => {
    const data = {};
    daysInMonth.forEach((date) => {
      const ds = format(date, "yyyy-MM-dd");
      const dateEntries = entriesByDate[ds] || [];
      if (dateEntries.length === 0) {
        data[ds] = [{
          id: null,
          halfDayType: "NONE",
          destination: "", flight: "", etd: "", eta: "", comment: "", others: "",
          inEu: false, inSin: false, specialOccasion: "", sharedSpecialOccasion: false,
          entryType: "TEMPLATE", category: "",
          flights: [{ destination: "", flight: "", etd: "", eta: "" }],
        }];
      } else {
        data[ds] = dateEntries.map((e) => ({
          id: e?.id || null,
          halfDayType: e?.halfDayType || "NONE",
          destination: e?.destination || "",
          flight: e?.flight || "",
          etd: extractTime(e?.etd),
          eta: extractTime(e?.eta),
          comment: e?.comment || "",
          others: e?.others || "",
          inEu: e?.inEu || false,
          inSin: e?.inSin || false,
          specialOccasion: e?.specialOccasion || "",
          sharedSpecialOccasion: e?.sharedSpecialOccasion || false,
          entryType: e?.entryType || "TEMPLATE",
          category: e?.category || "",
          flights: e?.flights && e.flights.length > 0
            ? e.flights.map((f) => ({
                destination: f.destination || "",
                flight: f.flight || "",
                etd: extractTime(f.etd),
                eta: extractTime(f.eta),
              }))
            : [{ destination: e?.destination || "", flight: e?.flight || "", etd: extractTime(e?.etd), eta: extractTime(e?.eta) }],
        }));
      }
    });
    setEditableData(data);
    setMultiEditMode(true);
    setSelectedEntryIds([]);
  };

  const handleExitMultiEdit = () => {
    setEditableData({});
    setMultiEditMode(false);
  };

  const handleMultiEditCellChange = useCallback((dateStr, entryIndex, field, value) => {
    setEditableData((prev) => {
      const rows = prev[dateStr] ? [...prev[dateStr]] : [];
      if (rows[entryIndex] !== undefined) {
        rows[entryIndex] = { ...rows[entryIndex], [field]: value };
      }
      return { ...prev, [dateStr]: rows };
    });
  }, []);

  const isRowDirty = useCallback((dateStr) => {
    const rows = editableData[dateStr];
    if (!rows) return false;
    const origEntries = entriesByDate[dateStr] || [];
    return rows.some((edited, i) => {
      const orig = origEntries[i] || null;
      if (!orig) {
        return !!(
          edited.destination ||
          edited.flight ||
          edited.etd ||
          edited.eta ||
          edited.comment ||
          edited.others ||
          edited.inEu ||
          edited.inSin ||
          edited.specialOccasion ||
          edited.halfDayType !== "NONE"
        );
      }
      const origEtd = extractTime(orig.etd);
      const origEta = extractTime(orig.eta);
      return (
        (edited.destination || "") !== (orig.destination || "") ||
        (edited.flight || "") !== (orig.flight || "") ||
        (edited.etd || "") !== origEtd ||
        (edited.eta || "") !== origEta ||
        (edited.comment || "") !== (orig.comment || "") ||
        (edited.others || "") !== (orig.others || "") ||
        !!edited.inEu !== !!orig.inEu ||
        !!edited.inSin !== !!orig.inSin ||
        (edited.specialOccasion || "") !== (orig.specialOccasion || "") ||
        !!edited.sharedSpecialOccasion !== !!orig.sharedSpecialOccasion ||
        (edited.entryType || "TEMPLATE") !== (orig.entryType || "TEMPLATE") ||
        (edited.halfDayType || "NONE") !== (orig.halfDayType || "NONE") ||
        (edited.category || "") !== (orig.category || "") ||
        (() => {
          const origFlights = orig?.flights && orig.flights.length > 0
            ? orig.flights.map((f) => ({ destination: f.destination || "", flight: f.flight || "", etd: extractTime(f.etd), eta: extractTime(f.eta) }))
            : [{ destination: orig?.destination || "", flight: orig?.flight || "", etd: origEtd, eta: origEta }];
          const editFlights = edited.flights || [{ destination: edited.destination || "", flight: edited.flight || "", etd: edited.etd || "", eta: edited.eta || "" }];
          return JSON.stringify(origFlights) !== JSON.stringify(editFlights);
        })()
      );
    });
  }, [editableData, entriesByDate]);

  const executeMultiEditSave = async (dirtyDates) => {
    setMultiEditSaving(true);
    try {
      const promises = dirtyDates.flatMap(({ dateStr, rows }) =>
        rows.map(({ row, existingId }) => {
          const payload = {
            userId: selectedUserId,
            entryDate: dateStr,
            destination: row.destination || null,
            flight: row.flight || null,
            etd: row.etd ? `${dateStr}T${row.etd}:00` : null,
            eta: row.eta ? `${dateStr}T${row.eta}:00` : null,
            comment: row.comment || null,
            others: row.others || null,
            inEu: row.inEu,
            inSin: row.inSin,
            specialOccasion: row.specialOccasion || null,
            sharedSpecialOccasion: row.sharedSpecialOccasion,
            approvalStatus: "WAITING_FOR_APPROVAL",
            entryType: row.entryType || "TEMPLATE",
            recordStatus: "ACTIVE",
            halfDayType: row.halfDayType || "NONE",
            category: row.category || null,
            flights: row.flights && row.flights.length > 0
              ? row.flights.map((f) => ({
                  destination: f.destination || null,
                  flight: f.flight || null,
                  etd: f.etd ? `${dateStr}T${f.etd}:00` : null,
                  eta: f.eta ? `${dateStr}T${f.eta}:00` : null,
                }))
              : null,
          };
          const apiCall = existingId
            ? updateTravelCalendarEntryApi(existingId, payload)
            : createTravelCalendarEntryApi(payload);
          return apiCall.then(() => (existingId ? "updated" : "created"));
        })
      );
      const results = await Promise.allSettled(promises);
      let created = 0, updated = 0, errors = 0;
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          if (r.value === "created") created++;
          else updated++;
        } else {
          errors++;
          console.error("Failed to save entry:", r.reason);
        }
      });
      if (errors > 0) {
        toast.error(`Saved with ${errors} error(s). ${created} created, ${updated} updated.`);
      } else {
        toast.success(`${created + updated} entries saved. (${created} new, ${updated} updated)`);
      }
      handleExitMultiEdit();
      loadEntries();
    } catch (err) {
      toast.error("Failed to save changes.");
    } finally {
      setMultiEditSaving(false);
    }
  };

  const handleMultiEditSave = async () => {
    const dirtyDates = Object.keys(editableData)
      .filter(isRowDirty)
      .map((dateStr) => ({
        dateStr,
        rows: editableData[dateStr].map((row) => ({ row, existingId: row.id })),
      }));
    if (dirtyDates.length === 0) {
      toast("No changes to save.");
      handleExitMultiEdit();
      return;
    }
    const missingCategory = dirtyDates.filter((d) => d.rows.some((r) => !r.row.category));
    if (missingCategory.length > 0) {
      const formattedDates = missingCategory.map((d) => {
        const dateObj = new Date(d.dateStr);
        return dateObj instanceof Date && !isNaN(dateObj)
          ? dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : d.dateStr;
      });
      toast.error(`Category is required. Missing on: ${formattedDates.join(", ")}`);
      return;
    }

    // Only check conflicts for brand-new rows (no existing id)
    const newDates = dirtyDates.filter((d) => d.rows.some((r) => !r.existingId));
    if (newDates.length === 0) {
      // All updates — no conflict check needed
      executeMultiEditSave(dirtyDates);
      return;
    }

    const sortedNewDates = [...newDates].sort((a, b) => a.dateStr.localeCompare(b.dateStr));
    retrieveTravelCalendarEntriesByDateRangeApi(
      sortedNewDates[0].dateStr,
      sortedNewDates[sortedNewDates.length - 1].dateStr
    )
      .then((res) => {
        const newDateSet = new Set(newDates.map((d) => d.dateStr));
        const others = (res?.data || []).filter(
          (e) =>
            e.user &&
            e.user.id !== selectedUserId &&
            e.entryDate &&
            newDateSet.has(e.entryDate.substring(0, 10))
        );
        if (others.length > 0) {
          setConflictingEntries(others);
          setPendingSavePayload(dirtyDates);
          setPendingSaveType("multiedit");
          setConflictDialogOpen(true);
        } else {
          executeMultiEditSave(dirtyDates);
        }
      })
      .catch(() => executeMultiEditSave(dirtyDates));
  };

  const dirtyCount = useMemo(
    () => (multiEditMode ? Object.keys(editableData).filter(isRowDirty).length : 0),
    [multiEditMode, editableData, entries, isRowDirty]
  );

  // ─── Render ───

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
        <Typography variant="h5" sx={{ color: "#1E2685", fontWeight: "bold", flexGrow: 1 }}>
          Travel Calendar
        </Typography>
        {isAdminOrManager && (
          <PendingApprovalPanel
            pendingEntries={pendingEntries}
            onNavigate={handlePendingEntryNavigate}
            onApprove={handlePendingApprove}
          />
        )}
      </Box>

      <CalendarToolbar
          onExportScreenshot={handleExportScreenshot}
        users={users}
        selectedUserId={selectedUserId}
        selectedUser={selectedUser}
        currentMonth={currentMonth}
        multiEditMode={multiEditMode}
        multiEditSaving={multiEditSaving}
        dirtyCount={dirtyCount}
        isOwnCalendar={isOwnCalendar}
        isDelegateForSelectedUser={isDelegateForSelectedUser}
        canEdit={canEdit}
        isAdminOrManager={isAdminOrManager}
        isManager={isManager}
        selectedEntryIds={selectedEntryIds}
        viewMode={viewMode}
        selectedYear={selectedYear}
        filterStartDate={filterStartDate}
        filterEndDate={filterEndDate}
        excelData={excelData}
        exportFileName={exportFileName}
        onFilterStartDateChange={setFilterStartDate}
        onFilterEndDateChange={setFilterEndDate}
        onUserChange={handleUserChange}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        onOpenMultiAdd={handleOpenMultiAdd}
        onEnterMultiEdit={handleEnterMultiEdit}
        onExitMultiEdit={handleExitMultiEdit}
        onMultiEditSave={handleMultiEditSave}
        onBulkDelete={handleBulkDelete}
        onBulkApprove={handleBulkApprove}
        onOpenBulkDisapprove={handleOpenBulkDisapprove}
        onToggleViewMode={() => setViewMode((v) => (v === "monthly" ? "yearly" : "monthly"))}
        onPrevYear={() => setSelectedYear((y) => y - 1)}
        onNextYear={() => setSelectedYear((y) => y + 1)}
        onManageDelegates={() => setManageDelegatesOpen(true)}
        onGoToMyCalendar={() => setSelectedUserId(currentUser?.id)}
      />

      {viewMode === "yearly" ? (
        <YearlyCalendar
          year={selectedYear}
          userId={selectedUserId}
          userName={selectedUser?.name || selectedUser?.username}
          isOwnCalendar={isOwnCalendar}
          isManager={isManager}
          yearlyRef={yearlyRef}
          onMonthClick={(monthIndex) => {
            setCurrentMonth(new Date(selectedYear, monthIndex, 1));
            setViewMode("monthly");
          }}
        />
      ) : (
        <div ref={tableRef} style={{ position: 'relative' }}>
          <CalendarTable
            daysInMonth={filteredDaysInMonth}
            entriesByDate={entriesByDate}
            sharedOccasions={sharedOccasions}
            selectedUserId={selectedUserId}
            showCheckboxes={showCheckboxes}
            selectedEntryIds={selectedEntryIds}
            allSelected={allSelected}
            multiEditMode={multiEditMode}
            editableData={editableData}
            isOwnCalendar={isOwnCalendar}
            canEdit={canEdit}
            isAdminOrManager={isAdminOrManager}
            isManager={isManager}
            onToggleSelectAll={handleToggleSelectAll}
            onToggleBulkSelect={handleToggleBulkSelect}
            onMultiEditCellChange={handleMultiEditCellChange}
            isRowDirty={isRowDirty}
            onEditEntry={handleEditEntry}
            onDeleteEntry={handleDeleteEntry}
            onAddEntry={handleAddEntry}
            onApproval={handleApproval}
            onOpenDisapprove={handleOpenDisapprove}
            onInspectEntry={handleInspectEntry}
            onLinkSaved={loadEntries}
            onDateOverview={handleDateOverview}
          />
        </div>
      )}

      <EntryDialog
        open={dialogOpen}
        editingEntry={editingEntry}
        selectedDate={selectedDate}
        selectedUser={selectedUser}
        formData={formData}
        onFormChange={setFormData}
        onSave={handleSave}
        onClose={() => setDialogOpen(false)}
      />

      <MultiAddDialog
        open={multiAddOpen}
        selectedUser={selectedUser}
        multiAddMonth={multiAddMonth}
        multiSelectedDates={multiSelectedDates}
        multiFormData={multiFormData}
        multiAddEntries={multiAddEntries}
        onMonthChange={handleMultiAddMonthChange}
        onToggleDate={handleToggleMultiDate}
        onFormChange={setMultiFormData}
        onSave={handleMultiSave}
        onClose={() => setMultiAddOpen(false)}
        onSetSelectedDates={setMultiSelectedDates}
      />

      <InspectDialog
        open={inspectDialogOpen}
        entry={inspectEntry}
        date={inspectDate}
        isAdminOrManager={isAdminOrManager}
        onApprove={handleInspectApprove}
        onDisapprove={handleInspectDisapprove}
        onClose={() => setInspectDialogOpen(false)}
      />

      <DisapproveDialog
        open={disapproveDialogOpen}
        note={disapproveNote}
        onNoteChange={setDisapproveNote}
        onConfirm={handleConfirmDisapprove}
        onClose={() => setDisapproveDialogOpen(false)}
      />

      <BulkDisapproveDialog
        open={bulkDisapproveOpen}
        count={selectedEntryIds.length}
        note={bulkDisapproveNote}
        onNoteChange={setBulkDisapproveNote}
        onConfirm={handleConfirmBulkDisapprove}
        onClose={() => setBulkDisapproveOpen(false)}
      />

      <ConflictPreviewDialog
        open={conflictDialogOpen}
        conflictingEntries={conflictingEntries}
        onProceed={handleConflictProceed}
        onGoBack={handleConflictGoBack}
      />

      <DelegateManagementDialog
        open={manageDelegatesOpen}
        onClose={() => setManageDelegatesOpen(false)}
        users={users}
        currentUserId={currentUser?.id}
      />

      <DateOverviewDialog
        open={dateOverviewOpen}
        date={dateOverviewDate}
        entries={dateOverviewEntries}
        loading={dateOverviewLoading}
        onClose={() => setDateOverviewOpen(false)}
      />

      {/* Screenshot preview dialog */}
      <Dialog
        open={!!screenshotPreview}
        onClose={() => setScreenshotPreview(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { m: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          Screenshot Preview
          <IconButton size="small" onClick={() => setScreenshotPreview(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 1, overflow: 'auto', textAlign: 'center', background: '#f5f5f5' }}>
          {screenshotPreview && (
            <img
              src={screenshotPreview.dataUrl}
              alt="Calendar screenshot"
              style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setScreenshotPreview(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={handleScreenshotDownload}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TravelCalendarView;
